import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { PencilSimple, X } from '@phosphor-icons/react';
import { Api, useApiFormSubmit } from '../../api';
import { produce } from 'immer';
import { useForm } from 'react-typed-form';
import { toast } from 'sonner';
import { iriToId } from '../../util';
import { FieldTextRequired } from '../../form';
import * as React from 'react';
import { useMutation } from 'api-read-hook';

interface EditNoteModalProps {
  userId: string;
  onClose: () => void;
  existing: Api.UserStats.V['notes'][0];
}

export default function EditNoteModal({
  userId,
  onClose,
  existing,
}: EditNoteModalProps) {
  const { mutateExact } = useMutation();

  const [isEditing, setIsEditing] = React.useState(false);
  const form = useForm<Api.UserNote.W>({
    defaultValues: existing,
    onSubmit: useApiFormSubmit<Api.UserNote.W, Api.UserNote.V>(
      'PATCH',
      `/user-notes/${iriToId(existing['@id'])}`,
      {
        onSuccess: (responseData) => {
          toast.success('Note updated');
          onClose();

          // Update data in User Dashboard without triggering full refresh
          mutateExact(
            `/users/${userId}/stats`,
            // @ts-expect-error todo[types]: solve issue
            (data: Api.UserStats.V) => {
              return produce(data, (draft) => {
                console.log(data, responseData);
                const index = draft.notes.findIndex(
                  (n) => n['@id'] === responseData['@id'],
                );
                if (index !== -1) {
                  draft.notes[index] = {
                    ...responseData,
                    addedByMe: true,
                    addedByName: 'you',
                  };
                }
              });
            },
          );
        },
      },
    ),
  });

  const canSubmit =
    form.getField('title').value?.trim() && form.getField('text').value?.trim();

  if (isEditing) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Edit Note</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <FieldTextRequired
                field={form.getField('title')}
                fullWidth
                containerSx={{ flex: 1 }}
              />
              <FieldTextRequired
                field={form.getField('date')}
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 200 }}
              />
            </Stack>

            <FieldTextRequired
              field={form.getField('text')}
              label={null}
              fullWidth
              multiline
              rows={12}
              placeholder="Write your note content here..."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={form.handleSubmit}
            variant="contained"
            disabled={!canSubmit || form.isLoading}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack gap={0.25}>
          <Typography variant="h6">{existing.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {existing.date} | by{' '}
            {existing.addedByMe ? 'you' : existing.addedByName}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1">{existing.text}</Typography>
      </DialogContent>

      {existing.addedByMe && (
        <DialogActions>
          <Button
            onClick={() => setIsEditing(true)}
            variant="text"
            startIcon={<PencilSimple />}
          >
            Edit
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
